import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { feedbackTemplatesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Trash2, GripVertical, ArrowLeft, Save, Eye, 
  Type, AlignLeft, Hash, FileText 
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_SCALE = {
  "10": "Surpasses required standard",
  "9": "Above required standard",
  "8": "Above required standard",
  "7": "Meets and satisfies required standard (PASS)",
  "6": "Meets and satisfies required standard (PASS)",
  "5": "Below required standard, needs improvement",
  "4": "Below required standard, needs improvement",
  "3": "Does NOT meet the required standard",
  "2": "Does NOT meet the required standard",
  "1": "Does NOT meet the required standard"
};

const DEFAULT_ELEMENTS = [
  {
    id: uuidv4(),
    type: "rating",
    label: "Culinary Knowledge",
    description: "Does the student show fundamental theoretical knowledge of food preparation in the working environment?",
    required: true,
    module: "WE 2"
  },
  {
    id: uuidv4(),
    type: "rating",
    label: "Culinary Skills",
    description: "Does the student demonstrate the necessary culinary skills, techniques of preparation and consistency within the performance of these skills?",
    required: true,
    module: "WE 2"
  },
  {
    id: uuidv4(),
    type: "rating",
    label: "Ability to Learn",
    description: "Does the student learn new tasks easily, quickly and thoroughly? Does he/she show enthusiasm about learning new skills?",
    required: true,
    module: "Personal Development"
  },
  {
    id: uuidv4(),
    type: "rating",
    label: "Initiative",
    description: "Is the student a self-starter using initiative and common sense when faced with a task or work load?",
    required: true,
    module: "WE 1 & Personal Development"
  },
  {
    id: uuidv4(),
    type: "rating",
    label: "Professional Attitude",
    description: "Does the student show discipline, sound work ethics, responsibility, punctuality and efficiency?",
    required: true,
    module: "WE 1"
  },
  {
    id: uuidv4(),
    type: "rating",
    label: "Professional Appearance",
    description: "Does the student maintain personal cleanliness and hygiene practices, wears the appropriate protective clothing and footgear?",
    required: true,
    module: "WE 1"
  }
];

const DEFAULT_INTRO = `Experiential training forms an important part of the student's overall growth and development in this industry, in order for a student to qualify as a professional chef at the ICA. We kindly request Mentors to assess our students and to comment on results and performances, highlighting strengths and weaknesses under the appropriate headings. We urge you to express your professional opinion to further equip the ICA to continuously attain quality and high standards in training of our young chefs. This report forms part of assessment and evidence of a student's overall performance and competency level. The ICA reserves the right to moderate the outcome of this report.`;

const FeedbackTemplateBuilder = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = !!templateId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    intro_text: DEFAULT_INTRO,
    is_default: false,
    assessment_scale: DEFAULT_SCALE,
    elements: DEFAULT_ELEMENTS
  });

  useEffect(() => {
    if (isEditing) {
      fetchTemplate();
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await feedbackTemplatesAPI.getOne(templateId);
      setFormData({
        name: response.data.name,
        description: response.data.description,
        intro_text: response.data.intro_text,
        is_default: response.data.is_default,
        assessment_scale: response.data.assessment_scale,
        elements: response.data.elements
      });
    } catch (error) {
      toast.error('Failed to load template');
      navigate('/feedback-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSaving(true);
    try {
      if (isEditing) {
        await feedbackTemplatesAPI.update(templateId, formData);
        toast.success('Template updated successfully');
      } else {
        await feedbackTemplatesAPI.create(formData);
        toast.success('Template created successfully');
      }
      navigate('/feedback-templates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addElement = (type) => {
    const newElement = {
      id: uuidv4(),
      type,
      label: type === 'section_header' ? 'New Section' : 'New Question',
      description: '',
      required: type !== 'section_header',
      module: ''
    };
    setFormData({ ...formData, elements: [...formData.elements, newElement] });
  };

  const updateElement = (id, field, value) => {
    setFormData({
      ...formData,
      elements: formData.elements.map(el => 
        el.id === id ? { ...el, [field]: value } : el
      )
    });
  };

  const removeElement = (id) => {
    setFormData({
      ...formData,
      elements: formData.elements.filter(el => el.id !== id)
    });
  };

  const moveElement = (index, direction) => {
    const newElements = [...formData.elements];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newElements.length) return;
    [newElements[index], newElements[newIndex]] = [newElements[newIndex], newElements[index]];
    setFormData({ ...formData, elements: newElements });
  };

  const updateScaleLabel = (score, label) => {
    setFormData({
      ...formData,
      assessment_scale: { ...formData.assessment_scale, [score]: label }
    });
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-500">Admin access required</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/feedback-templates')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-ica-navy">
                {isEditing ? 'Edit Template' : 'Create Feedback Template'}
              </h1>
              <p className="text-gray-500 text-sm">Design the assessment form for placement feedback</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? 'Edit' : 'Preview'}
            </Button>
            <Button onClick={handleSave} disabled={saving} >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </div>

        {showPreview ? (
          <FormPreview formData={formData} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form Editor */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Template Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Template Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Industry Assessment Report"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of this template"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Introduction Text</Label>
                    <Textarea
                      value={formData.intro_text}
                      onChange={(e) => setFormData({ ...formData, intro_text: e.target.value })}
                      rows={4}
                      placeholder="Instructions for the assessor..."
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_default}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                    />
                    <Label>Set as default template</Label>
                  </div>
                </CardContent>
              </Card>

              {/* Assessment Scale */}
              <Card>
                <CardHeader>
                  <CardTitle>Assessment Scale (1-10)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(score => (
                      <div key={score} className="flex items-center gap-2">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                          score >= 5 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {score}
                        </span>
                        <Input
                          value={formData.assessment_scale[score.toString()] || ''}
                          onChange={(e) => updateScaleLabel(score.toString(), e.target.value)}
                          className="flex-1 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Form Elements */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Assessment Criteria</CardTitle>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => addElement('rating')}>
                        <Hash className="w-4 h-4 mr-1" />
                        Rating
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('text')}>
                        <Type className="w-4 h-4 mr-1" />
                        Text
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('textarea')}>
                        <AlignLeft className="w-4 h-4 mr-1" />
                        Long Text
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => addElement('section_header')}>
                        <FileText className="w-4 h-4 mr-1" />
                        Section
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.elements.map((element, index) => (
                    <div key={element.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => moveElement(index, 'up')}
                            className="p-1 hover:bg-gray-200 rounded"
                            disabled={index === 0}
                          >
                            <GripVertical className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              element.type === 'rating' ? 'bg-blue-100 text-blue-700' :
                              element.type === 'section_header' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {element.type === 'rating' ? 'Rating (1-10)' :
                               element.type === 'section_header' ? 'Section Header' :
                               element.type === 'textarea' ? 'Long Text' : 'Text'}
                            </span>
                            {element.type !== 'section_header' && (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={element.required}
                                  onCheckedChange={(checked) => updateElement(element.id, 'required', checked)}
                                />
                                <span className="text-xs text-gray-500">Required</span>
                              </div>
                            )}
                          </div>
                          <Input
                            value={element.label}
                            onChange={(e) => updateElement(element.id, 'label', e.target.value)}
                            placeholder="Question/Label"
                            className="font-medium"
                          />
                          {element.type !== 'section_header' && (
                            <>
                              <Textarea
                                value={element.description}
                                onChange={(e) => updateElement(element.id, 'description', e.target.value)}
                                placeholder="Description/Help text"
                                rows={2}
                                className="text-sm"
                              />
                              <Input
                                value={element.module}
                                onChange={(e) => updateElement(element.id, 'module', e.target.value)}
                                placeholder="Module (e.g., WE 1, Personal Development)"
                                className="text-sm"
                              />
                            </>
                          )}
                        </div>
                        <button
                          onClick={() => removeElement(element.id)}
                          className="p-2 hover:bg-red-100 text-red-500 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {formData.elements.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No elements yet. Add rating questions, text fields, or section headers above.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Quick Tips</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-2">
                  <p>• <strong>Rating</strong> questions use the 1-10 scale</p>
                  <p>• <strong>Text</strong> for short answers</p>
                  <p>• <strong>Long Text</strong> for detailed feedback</p>
                  <p>• <strong>Section</strong> headers organize the form</p>
                  <p>• Drag elements to reorder them</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Standard Sections</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600">
                  <p className="mb-2">The form automatically includes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Strengths field</li>
                    <li>Weaknesses field</li>
                    <li>ICA Training feedback</li>
                    <li>General comments</li>
                    <li>Chef signature</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Preview Component
const FormPreview = ({ formData }) => {
  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-8">
        {/* Header */}
        <div className="text-center mb-8 border-b pb-6">
          <h1 className="text-2xl font-bold text-ica-navy mb-2">{formData.name || 'Feedback Form'}</h1>
          <p className="text-gray-500">{formData.description}</p>
        </div>

        {/* Intro */}
        {formData.intro_text && (
          <div className="bg-gray-50 p-4 rounded-lg mb-8 text-sm text-gray-700">
            {formData.intro_text}
          </div>
        )}

        {/* Scale Legend */}
        <div className="mb-8">
          <h3 className="font-semibold text-ica-navy mb-3">Assessment Scale</h3>
          <div className="grid grid-cols-5 gap-2 text-xs">
            {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(score => (
              <div key={score} className="flex items-center gap-1">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                  score >= 5 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {score}
                </span>
                <span className="text-gray-600 truncate">{formData.assessment_scale[score.toString()]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Elements */}
        <div className="space-y-6">
          {formData.elements.map((element, index) => (
            <div key={element.id}>
              {element.type === 'section_header' ? (
                <h3 className="text-lg font-semibold text-ica-navy border-b pb-2 mt-8">
                  {element.label}
                </h3>
              ) : element.type === 'rating' ? (
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium">{element.label} {element.required && <span className="text-red-500">*</span>}</p>
                      {element.module && <span className="text-xs text-ica-gold">({element.module})</span>}
                    </div>
                  </div>
                  {element.description && <p className="text-sm text-gray-600 mb-3">{element.description}</p>}
                  <div className="flex gap-2">
                    {[1,2,3,4,5,6,7,8,9,10].map(score => (
                      <button key={score} className="w-8 h-8 rounded-full border-2 border-gray-200 hover:border-ica-gold text-sm">
                        {score}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Input placeholder="Comments..." className="text-sm" />
                  </div>
                </div>
              ) : element.type === 'textarea' ? (
                <div>
                  <Label>{element.label} {element.required && <span className="text-red-500">*</span>}</Label>
                  {element.description && <p className="text-sm text-gray-500 mb-2">{element.description}</p>}
                  <Textarea rows={4} placeholder="Enter your response..." />
                </div>
              ) : (
                <div>
                  <Label>{element.label} {element.required && <span className="text-red-500">*</span>}</Label>
                  {element.description && <p className="text-sm text-gray-500 mb-2">{element.description}</p>}
                  <Input placeholder="Enter your response..." />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Standard Sections */}
        <div className="mt-8 pt-8 border-t space-y-6">
          <div>
            <Label>What strengths did the student demonstrate? *</Label>
            <Textarea rows={3} placeholder="Enter strengths..." className="mt-2" />
          </div>
          <div>
            <Label>What weaknesses did the student demonstrate? *</Label>
            <Textarea rows={3} placeholder="Enter weaknesses..." className="mt-2" />
          </div>
          <div>
            <Label>How would you rate the standard of ICA training?</Label>
            <Textarea rows={3} placeholder="Your feedback on the training program..." className="mt-2" />
          </div>
          <div>
            <Label>General comments</Label>
            <Textarea rows={4} placeholder="General comments on the student's performance..." className="mt-2" />
          </div>
        </div>

        {/* Signature Section */}
        <div className="mt-8 pt-8 border-t">
          <h3 className="font-semibold text-ica-navy mb-4">Chef Signature</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Chef Name</Label>
              <Input placeholder="Enter your name" className="mt-2" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" className="mt-2" />
            </div>
          </div>
          <div className="mt-4 border-2 border-dashed rounded-lg h-32 flex items-center justify-center text-gray-400">
            Signature Area
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackTemplateBuilder;
